// P05-5-1: Onboarding API Hooks
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface OnboardingSession {
  step: number;
  completed: boolean;
  data: Record<string, any>;
}

interface OnboardingData {
  skill_level?: 'beginner' | 'intermediate' | 'advanced' | 'low_handicap' | 'scratch_plus';
  handicap?: number | null;
  primary_goal?: string;
  region?: string;
  zip_code?: string;
  radius?: number;
  country?: 'US' | 'CA';
  coach_interests?: string[];
}

async function fetchOnboardingSession(): Promise<OnboardingSession> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/onboarding-start`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch onboarding session' }));
    throw new Error(error.error || 'Failed to fetch onboarding session');
  }

  return response.json();
}

async function saveOnboardingStep(step: number, data: Partial<OnboardingData>): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('Not authenticated');
  }

  // First, fetch existing session to merge data
  const { data: existingSession, error: fetchError } = await supabase
    .from('onboarding_sessions')
    .select('data')
    .eq('user_id', session.user.id)
    .single();

  // Merge existing data with new step data
  const mergedData = {
    ...(existingSession?.data || {}),
    ...data,
  };

  // Update session via Supabase directly (RLS allows user to update own session)
  const { error } = await supabase
    .from('onboarding_sessions')
    .upsert({
      user_id: session.user.id,
      step,
      data: mergedData,
      updated_at: new Date().toISOString()
    }, {
      onConflict: 'user_id'
    });

  if (error) {
    throw new Error(error.message || 'Failed to save onboarding step');
  }
}

async function completeOnboarding(data: OnboardingData): Promise<{ success: boolean; post_id?: string; groups_assigned?: Array<{ group_id: string; group_name: string }> }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/onboarding-complete`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ onboarding_data: data }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to complete onboarding' }));
    throw new Error(error.error || 'Failed to complete onboarding');
  }

  return response.json();
}

export function useOnboardingSession() {
  return useQuery({
    queryKey: ['onboarding', 'session'],
    queryFn: fetchOnboardingSession,
    retry: 1,
  });
}

export function useSaveOnboardingStep() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ step, data }: { step: number; data: Partial<OnboardingData> }) =>
      saveOnboardingStep(step, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'session'] });
    },
  });
}

export function useCompleteOnboarding() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: completeOnboarding,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'session'] });
      queryClient.invalidateQueries({ queryKey: ['profiles'] });
      queryClient.invalidateQueries({ queryKey: ['feed', 'posts'] });
    },
  });
}

